import React, {
  useContext,
  useState,
  useEffect,
  Suspense,
  useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import Routes from 'Routes';
import { RONGCLOUD_CHATROOM_PREFIX, EVENT_NAME, PAGE_NAMES } from 'appenv';
import { getFilterOptionByExhibitor, filterExhibitorsByKeywords } from 'models/exhibitors';

import XtraAppBar from 'components/appbar/XtraAppBar';
import Footer from 'components/Footer';
import BackgroundImg from 'assets/hall/background.jpg';
import ChatroomIcon from 'assets/chatroomWhite.svg';

import useURLSearchParams from 'hooks/useURLSearchParams';
import ChatContext from 'components/chat/ChatContext';
import BoothFilterSwitch from './BoothFilterSwitch';
import BoothPreview from './BoothPreview';

const RootContainerUI = styled.div`
  min-width: 100%;
  height: 100%;
  overflow: auto;

  @media (min-width: 960px) {
    background: url(${BackgroundImg});
    background-position: left top;
    background-repeat: no-repeat;
    background-size: 1440px auto;
  }

  @media (max-width: 960px) {
    background-color: #F0F1F2;
  }

  @media (min-width: 1440px), (min-height: 960px) {
    background-size: cover;
  }
`;

function RootContainer({ children }) {
  return (
    <RootContainerUI>{children}</RootContainerUI>
  );
}

RootContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

const BoothFilterListContainer = styled.div`
  width: 100%;
  height: calc(100% - 67.73px);
  display: inline-flex;

  @media (max-width: 959px) {
    padding-top: 56px;
    height: calc(100% - 56px);
    display: block;
  }
`;

const BreadcrumbsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 16px;

  @media (min-width: 960px) {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const BoothListContainer = styled.div`
  width: calc(100% - 300px);
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
  margin: auto;

  @media only screen and (max-width: 960px) {
    width: 100%;
  }
`;

const PopupBtn = styled.div`
  position: fixed;
  bottom: 50px;
  right: 20px;
  width: 60px;
  height: 60px;
  color: #FFF;
  text-align: center;
  line-height: 76px;
  background: #0CA58B;
  border-radius: 60px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
  z-index: 1100;
  cursor: pointer;
`;
// z-index: {ChatModal: 1300, PopupBtn:1100, BoothPreview: 1000}

const FilteringResultsText = styled.div`
  flex-basis: 100%;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.408px;
  text-transform: capitalize;
  color: #0CA58B;
  padding: 24px;
`;

const FooterWrapper = styled.div`
  align-items: flex-end;
  justify-content: center;
  display: flex;
  flex-basis: 100%;
`;

const isAllLeafKvPairEmpty = (selectedOptions) => {
  if (typeof selectedOptions === 'object' && selectedOptions !== null) {
    return Object.keys(selectedOptions)
      .every((key) => isAllLeafKvPairEmpty(selectedOptions[key]));
  }
  return (selectedOptions !== null && !!selectedOptions.length);
};

const HallPage = () => {
  const query = useURLSearchParams();
  const history = useHistory();

  const selectedFilter = useMemo(() => {
    try {
      const fromQuery = JSON.parse(query.get('selectedFilter'));
      const fromStorage = JSON.parse(localStorage.getItem('boothFilter'));
      return fromQuery || fromStorage || {};
    } catch {
      return {};
    }
  }, [query]);

  const setSelectedFilter = useCallback((filter) => {
    localStorage.removeItem('boothFilter');

    if (isAllLeafKvPairEmpty(filter)) {
      query.delete('selectedFilter');
    } else {
      query.set('selectedFilter', JSON.stringify(filter));
    }

    history.replace({
      pathname: Routes.hall,
      search: `?${query.toString()}`,
      state: history.location.state,
    });
  }, [query, history]);

  useEffect(() => {
    localStorage.setItem('boothFilter', JSON.stringify(selectedFilter));
  }, [selectedFilter]);

  const { joinGroupChat, setChatRoomTitle } = useContext(ChatContext);
  const openChatWindow = () => {
    window.trackingEvent('PublicChatroom_Enter', 'Global_Chatroom_Click', 'Hall');
    setChatRoomTitle(`${PAGE_NAMES.hall} (Public Chat)`);
    joinGroupChat(`${RONGCLOUD_CHATROOM_PREFIX}_hall`, 'group', 'Hall');
  };

  const subLevelOnClick = useCallback((category, index, subCategory) => {
    let newSearchResult;
    if (selectedFilter?.[subCategory]?.[category]) {
      if (selectedFilter[subCategory][category].includes(index)) {
        newSearchResult = {
          ...selectedFilter,
          [subCategory]: {
            ...selectedFilter[subCategory],
            [category]: selectedFilter[subCategory][category].filter((eachSelectedFilter) => eachSelectedFilter !== index),
          },
        };
      } else {
        newSearchResult = {
          ...selectedFilter,
          [subCategory]: {
            ...selectedFilter[subCategory],
            [category]: [...selectedFilter[subCategory][category], index],
          },
        };
      }
    } else {
      newSearchResult = {
        ...selectedFilter,
        [subCategory]: {
          ...selectedFilter[subCategory],
          [category]: [index],
        },
      };
    }
    setSelectedFilter(newSearchResult);
  }, [selectedFilter, setSelectedFilter]);

  const onClick = useCallback((category, index) => {
    let newSearchResult;
    if (selectedFilter[category]) {
      if (selectedFilter[category].includes(index)) {
        newSearchResult = {
          ...selectedFilter,
          [category]: selectedFilter[category].filter((eachSelectedFilter) => eachSelectedFilter !== index),
        };
      } else {
        newSearchResult = {
          ...selectedFilter,
          [category]: [...selectedFilter[category], index],
        };
      }
    } else {
      newSearchResult = {
        ...selectedFilter,
        [category]: [index],
      };
    }
    setSelectedFilter(newSearchResult);
  }, [selectedFilter, setSelectedFilter]);

  const fetchingExhibitors = useSelector((state) => state.exhibitors.fetching);
  const exhibitors = useSelector(filterExhibitorsByKeywords(selectedFilter));
  const filterOptions = useSelector(getFilterOptionByExhibitor());
  const isDesktop = useMediaQuery('(min-width: 960px)');

  return (
    <RootContainer>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${PAGE_NAMES.hall} | ${EVENT_NAME}`}</title>
        <meta name="description" content={`Hall page of ${EVENT_NAME}.`} />
      </Helmet>
      <PopupBtn onClick={openChatWindow}>
        <img src={ChatroomIcon} alt="chatroom" />
      </PopupBtn>
      <XtraAppBar />
      <BoothFilterListContainer>
        <BoothFilterSwitch
          filterOptions={filterOptions}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          resetSelectedFilter={() => setSelectedFilter({})}
          numOfExhibitors={exhibitors.length}
          subLevelOnClick={subLevelOnClick}
          onClick={onClick}
        />
        <BreadcrumbsContainer>
          <Breadcrumbs aria-label="breadcrumb" separator=">">
            <StyledLink to={Routes.lobby}>
              Lobby
            </StyledLink>
            <Typography color="textPrimary">
              Business and Investment Matching
            </Typography>
          </Breadcrumbs>
        </BreadcrumbsContainer>
        <BoothListContainer>
          {
            !isEmpty(selectedFilter) && isDesktop && (
              <FilteringResultsText>
                {`Filtering results: ${exhibitors.length}`}
              </FilteringResultsText>
            )
          }
          {
            !fetchingExhibitors ? (
              exhibitors.map((booth, index) => (
                <BoothPreview
                  index={index}
                  key={booth.id}
                  booth={booth}
                />
              ))
            ) : (
              <CircularProgress />
            )
          }
          <FooterWrapper>
            <Footer hashState="/hall" />
          </FooterWrapper>
        </BoothListContainer>
      </BoothFilterListContainer>
    </RootContainer>
  );
};

export default HallPage;
